import { useGetCountries, useGetProduceTypes, useGetSpecifyIngredient } from "../../../../../api/static-data";
import { Card, CardContent } from "../../../../../components/ui/common/card";
import { useIngredientFlags } from "../../../../../hooks/useIngredientFlags";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import { ClimateImpactFormatter } from "../../../ingredientList/services/ClimateImpactFormatter";
import { IngredientRegisterOptions } from "../../../ingredientRegister/services/IngredientRegisterOptions";
import { IngredientDetailsFormatter } from "../../services/IngredientDetailsFormatter";
import ReadOnlyField from "../shared/ReadOnlyField";

interface EnvironmentalReadOnlyCardProps {
  ingredientDetails: IngredientRowViewModel | undefined;
  isUserSupplier: boolean;
}

const CARD_HEADER_CLASS = "tw-text-sm tw-font-semibold tw-text-primary";

/**
 * "Environmental Data" read-only card — whichever of the RAW (Produce
 * Type/Cultivar/Farming/Field) or PROCESSED (Specify Ingredient) field
 * groups apply, plus Country and Climate Change Impact (kgCO2eq/kg). Unit
 * Size/Unit Type/EAN Barcode moved out to `UnitDetailsTable`. Tailwind
 * rebuild of the legacy `IngredientDetailsView`/`ProduceIngredientView`
 * (src/screens/ingredients/ingredientDetails/), reusing the same option
 * lists (`IngredientRegisterOptions`) the register form uses so ids resolve
 * to readable text.
 */
const EnvironmentalReadOnlyCard = ({ ingredientDetails, isUserSupplier }: EnvironmentalReadOnlyCardProps) => {
  const details = ingredientDetails?.supplierIngredientDetails as
    | (Record<string, unknown> & {
        unit_size?: number;
        unit_type?: string;
        ean_bar_code?: string;
        countryOrigin?: number;
        produceType?: string;
        produceCultivar?: string;
        farmingType?: string;
        fieldType?: string;
        specifyIngredient?: string;
      })
    | null
    | undefined;

  const { produceIngredient, processIngredient } = useIngredientFlags(
    details?.ingredient_type as any,
    details?.data_type as any
  );

  const { data: countriesData } = useGetCountries();
  const { data: produceTypesData } = useGetProduceTypes();
  const { data: specifyIngredientData } = useGetSpecifyIngredient();

  if (!details?.data_type) return null;

  const countryOptions = IngredientRegisterOptions.countries(
    countriesData as unknown as Array<{ id: number; country: string }>
  );
  const produceTypeOptions = IngredientRegisterOptions.produceTypes(
    produceTypesData as unknown as Array<{ id: number; produce_name: string }>
  );
  const farmingTypeOptions = IngredientRegisterOptions.farmingTypes();
  const fieldTypeOptions = IngredientRegisterOptions.fieldTypes();
  const specifyIngredientOptions = IngredientRegisterOptions.specifyIngredients(specifyIngredientData);

  const climateChangeImpact = ingredientDetails
    ? ClimateImpactFormatter.format(ingredientDetails, isUserSupplier)
    : "-";

  return (
    <Card className="tw-gap-0 tw-overflow-hidden tw-rounded-lg tw-py-0">
      <div className="tw-border-b tw-border-primary/20 tw-px-5 tw-py-3">
        <p className={CARD_HEADER_CLASS}>Environmental Data</p>
      </div>
      <CardContent className="tw-flex tw-flex-col tw-gap-5 tw-p-5">
        {produceIngredient ? (
          <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
            <ReadOnlyField
              label="Produce Type"
              value={IngredientDetailsFormatter.optionLabel(produceTypeOptions, details?.produceType)}
            />
            <ReadOnlyField label="Produce Cultivar" value={details?.produceCultivar} />
            <ReadOnlyField
              label="Farming Type"
              value={IngredientDetailsFormatter.optionLabel(farmingTypeOptions, details?.farmingType)}
            />
            <ReadOnlyField
              label="Field Type"
              value={IngredientDetailsFormatter.optionLabel(fieldTypeOptions, details?.fieldType)}
            />
            <ReadOnlyField
              label="Country"
              value={IngredientDetailsFormatter.optionLabel(countryOptions, details?.countryOrigin)}
            />
            <ReadOnlyField label="Climate Change Impact (kgCO2eq/kg)" value={climateChangeImpact} mono />
          </div>
        ) : null}

        {processIngredient ? (
          <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
            <ReadOnlyField
              label="Specify Ingredient"
              value={IngredientDetailsFormatter.optionLabel(
                specifyIngredientOptions,
                details?.specifyIngredient
              )}
            />
            <ReadOnlyField
              label="Country"
              value={IngredientDetailsFormatter.optionLabel(countryOptions, details?.countryOrigin)}
            />
            <ReadOnlyField label="Climate Change Impact (kgCO2eq/kg)" value={climateChangeImpact} mono />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default EnvironmentalReadOnlyCard;
